import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function WordingPreview({ label, sample, testid }) {
  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="text-sm"><span className="font-semibold">In-app:</span> {sample.in_app_title}</div>
      <p className="text-xs text-muted-foreground">{sample.in_app_body}</p>
      <div className="text-sm"><span className="font-semibold">Email subject:</span> {sample.email_subject}</div>
      <iframe
        title={`${label} email`}
        srcDoc={sample.email_html.replace("<head>", '<head><base target="_blank">')}
        className="w-full h-[420px] rounded-lg border bg-white"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        data-testid={`${testid}-iframe`}
      />
      <p className="text-[11px] text-muted-foreground">Links in this preview open in a new tab. Viewing the project page as an admin never signs or changes anything.</p>
    </div>
  );
}

function RecipientTable({ rows }) {
  return (
    <div className="max-h-[320px] overflow-auto rounded-xl border border-border pb-14 sm:pb-0" data-testid="catchup-recipient-table">
      <table className="w-full text-xs">
        <thead className="bg-muted sticky top-0 z-10">
          <tr><th className="text-left p-2">Reader</th><th className="text-left p-2">Project</th><th className="text-left p-2">Needs</th><th className="text-left p-2">Email</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.reader_id}-${r.job_id}`} className="border-t border-border/60" data-testid={`catchup-row-${r.reader_id}-${r.job_id}`}>
              <td className="p-2">{r.reader_name}</td>
              <td className="p-2">{r.job_title}</td>
              <td className="p-2">{r.variant === "update" ? `v2.0 (signed v${r.previous_version})` : "First signature"}</td>
              <td className="p-2">{!r.email_eligible ? "No verified email" : r.opted_out_of_updates ? "Opted out of optional emails" : "Yes"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ConfidentialityCatchupCard() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeOptedOut, setIncludeOptedOut] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!confirming) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !sending) setConfirming(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [confirming, sending]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/confidentiality-catchup/preview"),
        api.get("/admin/confidentiality-catchup/runs"),
      ]);
      setData(p.data);
      setRuns(r.data);
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/confidentiality-catchup/send", {
        confirm: true, expected_count: data.recipients.length, include_opted_out: includeOptedOut,
      });
      toast.success(`Catch-up started for ${res.in_app_queued} readers. Refresh the preview in a minute to confirm.`);
      setConfirming(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const c = data?.counts;
  const emailCount = c ? c.email + (includeOptedOut ? c.opted_out_but_essential : 0) : 0;
  return (
    <section className="card-soft space-y-4" data-testid="catchup-card">
      <div className="flex items-start gap-3">
        <ShieldAlert size={20} className="text-[hsl(var(--sb-clay-2))] shrink-0 mt-1" />
        <div className="min-w-0">
          <h2 className="font-serif text-2xl">Confidentiality catch-up</h2>
          <p className="text-sm text-muted-foreground">One-time notice for accepted readers who still need to accept the current agreement. Nothing is sent until you confirm. Agreements are never signed or unlocked for anyone.</p>
        </div>
      </div>
      <button type="button" className="btn-ghost border min-h-[44px]" onClick={load} disabled={loading} data-testid="catchup-preview-btn">
        <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {data ? "Refresh preview" : "Preview recipients & wording"}
      </button>
      {data && runs.length > 0 && (
        <div className="rounded-xl border p-3 text-xs" data-testid="catchup-last-run">
          <span className="font-semibold">Last run:</span>{" "}
          <span data-testid="catchup-last-run-status">{runs[0].status}</span>
          {" · "}started {new Date(runs[0].started_at).toLocaleString()}
          {runs[0].status !== "running" && <> · {runs[0].in_app_sent ?? 0} in-app, {runs[0].emails_sent ?? 0} emails{runs[0].errors ? `, ${runs[0].errors} errors` : ""}</>}
        </div>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="catchup-counts">
            <div className="rounded-xl border p-2"><div className="text-xl font-semibold" data-testid="catchup-count-in-app">{c.in_app}</div>in-app notices</div>
            <div className="rounded-xl border p-2"><div className="text-xl font-semibold" data-testid="catchup-count-email">{emailCount}</div>emails</div>
            <div className="rounded-xl border p-2"><div className="text-xl font-semibold" data-testid="catchup-count-unsigned">{c.unsigned}</div>never signed</div>
            <div className="rounded-xl border p-2"><div className="text-xl font-semibold" data-testid="catchup-count-update">{c.update}</div>need v2.0 update</div>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="catchup-skipped">
            Skipped: {data.skipped.already_current} already on v{data.version}, {data.skipped.already_notified} already notified,
            {" "}{data.skipped.declined} declined, {data.skipped.engagement_closed} finished engagements,
            {" "}{data.skipped.inactive_project} applications on archived/closed/demo projects, {data.skipped.test_account} demo/QA accounts, {data.skipped.accepted_under_new_flow} accepted after the agreement existed (already notified at acceptance).
          </p>
          <RecipientTable rows={data.recipients} />
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="h-5 w-5" checked={includeOptedOut} onChange={(e) => setIncludeOptedOut(e.target.checked)} data-testid="catchup-include-opted-out" />
            Also email the {c.opted_out_but_essential} reader{c.opted_out_but_essential === 1 ? "" : "s"} who turned off optional emails (sent as an essential project email)
          </label>
          <div className="grid gap-3 lg:grid-cols-2">
            <WordingPreview label="Never signed" sample={data.samples.unsigned} testid="catchup-wording-unsigned" />
            <WordingPreview label="Signed v1.0, needs v2.0" sample={data.samples.update} testid="catchup-wording-update" />
          </div>
          <button type="button" className="btn-primary min-h-[44px] disabled:opacity-50" disabled={!c.in_app} onClick={() => setConfirming(true)} data-testid="catchup-send-btn">
            Send catch-up to {c.in_app} reader{c.in_app === 1 ? "" : "s"}
          </button>
        </>
      )}
      {confirming && createPortal(
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-3" data-testid="catchup-confirm-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !sending && setConfirming(false)} />
          <div className="relative max-w-md w-full rounded-2xl border border-neutral-300 bg-white text-neutral-900 p-5 space-y-3 shadow-xl">
            <h3 className="font-serif text-2xl">Send catch-up notices?</h3>
            <p className="text-sm">{c.in_app} in-app notice{c.in_app === 1 ? "" : "s"} and {emailCount} email{emailCount === 1 ? "" : "s"} will be sent now to real readers. Each reader gets this once per project.</p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button className="rounded-full border border-neutral-400 px-4 min-h-[44px] text-neutral-900" onClick={() => setConfirming(false)} disabled={sending} data-testid="catchup-confirm-cancel">Cancel</button>
              <button className="btn-primary min-h-[44px]" onClick={send} disabled={sending} data-testid="catchup-confirm-send">{sending ? "Starting…" : "Send now"}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
